import React, { useState, useEffect } from "react";
import { Row, Col, Spin, message, Typography, Space, Alert } from "antd";
import { useQuery } from "react-query";
import {
  getSequenceAnalyticsLive,
  getSequenceQueueStatus,
  getSequenceProcessingMetrics,
} from "@api/email/emailSequence";
import {
  SequenceStatusCard,
  SequenceQueueOverview,
  SequenceActivityFeed,
} from "./index";
import { SyncOutlined, ThunderboltOutlined } from "@ant-design/icons";

const { Title } = Typography;

const REFRESH_INTERVAL = 30000; // 30 seconds

const SequenceAnalyticsTab = () => {
  const [refreshKey, setRefreshKey] = useState(0);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshKey(prev => prev + 1);
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  // Fetch live analytics data
  const {
    data: liveData,
    isLoading: liveLoading,
    error: liveError,
    refetch: refetchLive,
    isFetching: liveFetching
  } = useQuery(
    ["sequenceAnalyticsLive", refreshKey],
    async () => {
      const response = await getSequenceAnalyticsLive();
      return response.data.data;
    },
    {
      refetchInterval: REFRESH_INTERVAL,
      staleTime: 25000,
      cacheTime: 35000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      onError: (error) => {
        console.error("Failed to fetch live sequence analytics:", error);
        message.error("Failed to fetch live sequence data");
      }
    }
  );

  // Fetch queue status
  const {
    data: queueData,
    isLoading: queueLoading,
    error: queueError
  } = useQuery(
    ["sequenceQueueStatus", refreshKey],
    async () => {
      const response = await getSequenceQueueStatus();
      return response.data.data;
    },
    {
      refetchInterval: REFRESH_INTERVAL,
      staleTime: 25000,
      cacheTime: 35000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      onError: (error) => {
        console.error("Failed to fetch sequence queue status:", error);
      }
    }
  );

  // Fetch processing metrics
  const {
    data: metricsData,
    isLoading: metricsLoading,
    error: metricsError
  } = useQuery(
    ["sequenceProcessingMetrics", refreshKey],
    async () => {
      const response = await getSequenceProcessingMetrics("24h");
      return response.data.data;
    },
    {
      refetchInterval: REFRESH_INTERVAL,
      staleTime: 25000,
      cacheTime: 35000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      onError: (error) => {
        console.error("Failed to fetch processing metrics:", error);
      }
    }
  );

  const isLoading = liveLoading || queueLoading || metricsLoading;
  const hasError = liveError || queueError || metricsError;

  if (hasError) {
    return (
      <div style={{ padding: "20px" }}>
        <Alert
          message="Error Loading Sequence Analytics"
          description="There was an error loading the sequence analytics data. Please try refreshing the page."
          type="error"
          showIcon
          action={
            <Space>
              <button 
                onClick={() => {
                  refetchLive();
                  setRefreshKey(prev => prev + 1);
                }}
                style={{
                  border: "none",
                  background: "#f5222d",
                  color: "white",
                  padding: "4px 12px",
                  borderRadius: "4px",
                  cursor: "pointer"
                }}
              >
                Retry
              </button>
            </Space>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: "0 0 20px 0" }}>

      {isLoading && !liveData ? (
        <div style={{ 
          textAlign: "center", 
          padding: "60px 0",
          background: "#fafafa",
          borderRadius: "12px"
        }}>
          <Spin size="large" />
          <Typography.Text style={{ display: "block", marginTop: "16px", color: "#666" }}>
            Loading sequence analytics...
          </Typography.Text>
        </div>
      ) : (
        <>
          {/* Queue Overview */}
          <Row style={{ marginBottom: "20px" }}>
            <Col span={24}>
              <SequenceQueueOverview queueData={queueData} />
            </Col>
          </Row>

          {/* Main Content */}
          <Row gutter={[20, 20]}>
            {/* Active Sequences */}
            <Col xs={24} lg={16}>
              <div style={{ marginBottom: "20px" }}>
                <Title level={4} style={{ marginBottom: "16px" }}>
                  Active Sequences ({liveData?.totalActiveSequences || 0})
                </Title>
                
                {liveData?.activeSequences?.length > 0 ? (
                  <Row gutter={[16, 16]}>
                    {liveData.activeSequences.slice(0, 6).map((sequence, index) => (
                      <Col xs={24} sm={12} lg={12} xl={8} key={sequence._id || index}>
                        <SequenceStatusCard sequence={sequence} />
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div style={{ 
                    textAlign: "center", 
                    padding: "40px",
                    background: "#fafafa",
                    borderRadius: "8px",
                    border: "1px dashed #d9d9d9"
                  }}>
                    <Typography.Text type="secondary">
                      No active sequences found
                    </Typography.Text>
                  </div>
                )}

                {liveData?.activeSequences?.length > 6 && (
                  <div style={{ 
                    textAlign: "center", 
                    padding: "16px",
                    background: "#f0f2ff",
                    borderRadius: "8px",
                    marginTop: "16px"
                  }}>
                    <Typography.Text type="secondary">
                      Showing top 6 sequences. 
                      {liveData.activeSequences.length - 6} more sequences are active.
                    </Typography.Text>
                  </div>
                )}
              </div>
            </Col>

            {/* Activity Feed */}
            <Col xs={24} lg={8}>
              <SequenceActivityFeed activityData={liveData?.recentActivity} />
            </Col>
          </Row>

          {/* Performance Metrics Summary */}
          {metricsData && (
            <Row style={{ marginTop: "20px" }}>
              <Col span={24}>
                <div style={{
                  background: "#f9f9f9",
                  padding: "16px",
                  borderRadius: "8px",
                  textAlign: "center"
                }}>
                  <Space size="large">
                    <div>
                      <Typography.Text strong style={{ fontSize: "18px", color: "#1890ff" }}>
                        {metricsData.emails?.sent || 0}
                      </Typography.Text>
                      <Typography.Text style={{ display: "block", fontSize: "12px", color: "#666" }}>
                        Emails Sent (24h)
                      </Typography.Text>
                    </div>
                    <div>
                      <Typography.Text strong style={{ fontSize: "18px", color: "#52c41a" }}>
                        {Math.round(metricsData.emails?.deliveryRate || 0)}%
                      </Typography.Text>
                      <Typography.Text style={{ display: "block", fontSize: "12px", color: "#666" }}>
                        Delivery Rate
                      </Typography.Text>
                    </div>
                    <div>
                      <Typography.Text strong style={{ fontSize: "18px", color: "#faad14" }}>
                        {Math.round(metricsData.emails?.openRate || 0)}%
                      </Typography.Text>
                      <Typography.Text style={{ display: "block", fontSize: "12px", color: "#666" }}>
                        Open Rate
                      </Typography.Text>
                    </div>
                    <div>
                      <Typography.Text strong style={{ fontSize: "18px", color: "#722ed1" }}>
                        {metricsData.sequences?.started || 0}
                      </Typography.Text>
                      <Typography.Text style={{ display: "block", fontSize: "12px", color: "#666" }}>
                        New Sequences Started
                      </Typography.Text>
                    </div>
                  </Space>
                </div>
              </Col>
            </Row>
          )}

          {/* Last Updated Info */}
          <div style={{ 
            textAlign: "center", 
            marginTop: "20px",
            padding: "8px",
            fontSize: "11px",
            color: "#8c8c8c"
          }}>
            Last updated: {liveData?.lastUpdated ? 
              new Date(liveData.lastUpdated).toLocaleTimeString() : 
              "Never"
            } • Auto-refreshes every 30 seconds
          </div>
        </>
      )}
    </div>
  );
};

export default SequenceAnalyticsTab;